import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface TranslationStatusBadgeProps {
  needsUpdate: boolean;
  frenchTimestamp: Date | null;
  currentTimestamp: Date | null;
  loading?: boolean;
}

export const TranslationStatusBadge = ({
  needsUpdate,
  frenchTimestamp,
  currentTimestamp,
  loading = false,
}: TranslationStatusBadgeProps) => {
  if (loading) {
    return (
      <Badge variant="outline" className="gap-2">
        <span className="animate-pulse">Vérification...</span>
      </Badge>
    );
  }

  if (!needsUpdate) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className="gap-2 bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3" />
              Traduction à jour
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Cette traduction est synchronisée avec la version française</p>
            {currentTimestamp && (
              <p className="text-xs text-muted-foreground mt-1">
                Dernière mise à jour : {format(currentTimestamp, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge variant="outline" className="gap-2 bg-orange-50 text-orange-700 border-orange-200 animate-pulse">
            <AlertCircle className="h-3 w-3" />
            Retraduction nécessaire
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-semibold">Le contenu français a été modifié</p>
          {frenchTimestamp && (
            <p className="text-xs text-muted-foreground mt-1">
              Version française : {format(frenchTimestamp, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
            </p>
          )}
          {currentTimestamp && (
            <p className="text-xs text-muted-foreground">
              Cette traduction : {format(currentTimestamp, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
            </p>
          )}
          <p className="text-xs mt-2 text-orange-600">
            Cliquez sur "Retraduire" pour mettre à jour
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
